import React, { useMemo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { TextField } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { isProductCustomizable } from '../../utils/selectors';
import ToggleTabs from '../ToggleTabs/ToggleTabs';

const Wrapper = styled.div`
  ${ToggleTabs.TabButton} {
    flex-basis: 50%;
  }
`;

const TabDescription = styled.div`
  font-size: 12px;
  font-weight: 300;
  line-height: 14px;
  padding: ${({ theme }) => theme.spacing.small}px;
`;

const CustomContentContainer = styled.div``;

const CustomContent = styled.div`
  padding: ${({ theme }) => theme.spacing.small}px;
  border: 1px solid ${({ theme }) => theme.colors.lavender250};
`;

const CustomContentHeader = styled.div`
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
`;

const CustomContentBulletsList = styled.ul`
  margin-top: 4px;
  margin-bottom: 10px;
  list-style: initial;
  margin-left: ${({ theme }) => theme.spacing.small}px;
`;

const CustomContentBullet = styled.li`
  font-size: 12px;
  font-weight: 300;
  line-height: 14px;
  margin: 4px 0px;
`;

const errorStyle = css`
  ${props => props.theme.errorInput.defaultStyle};
`;

const inputBoxStyle = css`
  ${props => props.theme.inputBox.defaultStyle};
`;

const inputStyle = css`
  ${props => props.theme.input.defaultStyle};
`;

const labelStyle = css`
  ${props => props.theme.labelInput.defaultStyle};
`;

const hintStyle = css`
  ${props => props.theme.hintInput.defaultStyle};
`;

const StyledTextField = styled(TextField).attrs({
  errorStyle: errorStyle,
  hintStyle: hintStyle,
  inputBoxStyle: inputBoxStyle,
  inputStyle: inputStyle,
  labelStyle: labelStyle,
})``;

const PreviewTitle = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.subdued};
  margin: ${({ theme }) => theme.spacing.tiny}px 0px;
`;

const PreviewBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  text-transform: uppercase;
  padding: ${({ theme }) => theme.spacing.tiny}px 12px;
  font-weight: bold;
  text-align: center;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lavender250};
  background: ${({ theme }) => theme.colors.gray150};
  white-space: pre;
`;

const CustomTextPlaceHolder = styled.div`
  opacity: 0.4;
`;

const TabNames = {
  STANDARD: 'standard',
  CUSTOM: 'custom',
};

export default function ProductDetailCustomization({
  customText,
  customTextError,
  product = {},
  onCustomTextChange,
}) {
  const isCustomizable = useSelector(state =>
    isProductCustomizable(state, { product })
  );
  const shouldShowToggleExperience = !!(
    product.linked_personalized_product &&
    product.linked_personalized_product.master_product_id
  );
  const tabs = [
    {
      name: TabNames.STANDARD,
      tabTitle: (
        <FormattedMessage
          id="site_pdp.standard_tab_title"
          defaultMessage="Standard"
        />
      ),
    },
    {
      name: TabNames.CUSTOM,
      tabTitle: (
        <FormattedMessage
          id="site_pdp.custom_tab_title"
          defaultMessage="Custom"
        />
      ),
    },
  ];

  const renderCustomTextError = customTextError => {
    switch (customTextError) {
      case 'ERROR_EMPTY_VALUE':
        return (
          <FormattedMessage
            id="site_pdp.custom_text_empty_value_error"
            defaultMessage="Enter your name"
          />
        );
      case 'ERROR_FORBIDDEN_CHARACTER':
        return (
          <FormattedMessage
            id="site_pdp.custom_text_forbidden_character_error"
            defaultMessage="Contains forbidden characters"
          />
        );
      case 'ERROR_INVALID_LENGTH':
        return (
          <FormattedMessage
            id="site_pdp.custom_text_invalid_length_error"
            defaultMessage="10 characters maximum"
          />
        );
      default:
        return null;
    }
  };

  const productCustomizationContent = useMemo(
    () =>
      isCustomizable ? (
        <CustomContentContainer>
          <TabDescription>
            <FormattedMessage
              id="site_pdp.custom_tab_description"
              defaultMessage="Custom sets will have “SavageX” & your custom text printed on each piece in the set."
            />
          </TabDescription>
          <CustomContent>
            <CustomContentHeader>
              <FormattedMessage
                id="site_pdp.custom_content_header"
                defaultMessage="Add your name to your set"
              />
            </CustomContentHeader>
            <CustomContentBulletsList>
              <CustomContentBullet>
                <FormattedMessage
                  id="site_pdp.custom_tab_shipping_period_bullet"
                  defaultMessage="Allow 4-6 weeks for customization and shipping from when the order is placed. "
                />
              </CustomContentBullet>
              <CustomContentBullet>
                <FormattedMessage
                  id="site_pdp.custom_tab_non_refundable_bullet"
                  defaultMessage="All customized set orders are non-refundable."
                />
              </CustomContentBullet>
              <CustomContentBullet>
                <FormattedMessage
                  id="site_pdp.custom_tab_text_format_bullet"
                  defaultMessage="Customized name must not exceed 10 characters and may exclude special characters."
                />
              </CustomContentBullet>
              <CustomContentBullet>
                <FormattedMessage
                  id="site_pdp.custom_tab_text_font_bullet"
                  defaultMessage="The text is only available in one font."
                />
              </CustomContentBullet>
            </CustomContentBulletsList>
            <StyledTextField
              value={customText}
              label={
                <FormattedMessage
                  id="site_pdp.customization_input_label"
                  defaultMessage="Your name"
                />
              }
              error={renderCustomTextError(customTextError)}
              onChange={onCustomTextChange}
            />
            <PreviewTitle>
              <FormattedMessage
                id="site_pdp.custom_content_preview_title"
                defaultMessage="Preview"
              />
            </PreviewTitle>
            <PreviewBox>
              {`Savage x`}
              &nbsp;
              {customText || (
                <CustomTextPlaceHolder>
                  <FormattedMessage
                    id="site_pdp.custom_text_place_holder"
                    defaultMessage="Your Text"
                  />
                </CustomTextPlaceHolder>
              )}
            </PreviewBox>
          </CustomContent>
        </CustomContentContainer>
      ) : null,
    [customText, customTextError, isCustomizable, onCustomTextChange]
  );

  const renderTabContent = useCallback(
    selectedTab => {
      switch (selectedTab.name) {
        case TabNames.STANDARD:
          return (
            <TabDescription>
              <FormattedMessage
                id="site_pdp.standard_tab_description"
                defaultMessage="Standard sets will have “SavageXFenty” printed on the bands."
              />
            </TabDescription>
          );
        case TabNames.CUSTOM:
          return productCustomizationContent;
        default:
          return null;
      }
    },
    [productCustomizationContent]
  );

  return shouldShowToggleExperience ? (
    <Wrapper>
      <ToggleTabs tabs={tabs} defaultTabIndex={isCustomizable ? 1 : 0}>
        {({ selectedTab }) => renderTabContent(selectedTab)}
      </ToggleTabs>
    </Wrapper>
  ) : (
    productCustomizationContent
  );
}

ProductDetailCustomization.propTypes = {
  customText: PropTypes.string,
  customTextError: PropTypes.string,
  product: PropTypes.object,
  onCustomTextChange: PropTypes.func,
};
